import { getQueryParam } from "../common/UrlUtils";
import { translate } from "../i18n/translate";
import { ArticleListingCard } from "../listings/components/ArticleListingCard";
import { globalSearchService } from "../services/GlobalSearchService";
import languageService from "../services/LanguageService";
import { onDomReadyAsync } from "../common/DomUtils";
import { SearchFilter } from "../search/components/SearchFilter";
import { DateRangeSearchFilter } from "../search/components/DateRangeSearchFilter";
import { SearchContext } from "../contexts/SearchContext";
import { DateRange } from "../models/DateRange";
import { GlobalSearchFacet } from "../models/GlobalSearchFacet";
import { translationService } from "../i18n/TranslationsService";

export const initializeSearchResultsPage = () => {
    onDomReadyAsync(async () => {
        const searchResultHeader = document.querySelector('#pwcstudio-searchresults-header') as HTMLElement;
        const searchTermDisplay = document.querySelector('#search-term-display') as HTMLElement;
        const searchBox = document.querySelector('#search') as HTMLInputElement;
        const searchForm = document.querySelector('#search-form') as HTMLFormElement;
        const searchTerm = getQueryParam('term');
        const filterContainer = document.querySelector('#filter-container') as HTMLElement;
        const horizontalFilterContent = document.querySelector('#horizontal-filter-content') as HTMLElement;
        const clearAllFiltersBtn = document.querySelector('#clear-all-filters-btn') as HTMLButtonElement;
        const resultsCountSpan = document.querySelector('#results-count') as HTMLElement;
        
        const searchContext = new SearchContext();
        const filterInstances: { [key: string]: SearchFilter | DateRangeSearchFilter | any } = {};

        // Initialize search term
        if (searchTerm) {
            const text = translate('Search.ResultsFor');
            if (searchTermDisplay) {
                searchTermDisplay.textContent = searchTerm;
            }
            if (searchBox) {
            searchBox.value = searchTerm;
            }
            globalSearchService.setSearchTerm(searchTerm);
        } else {
            if (searchTermDisplay) {
                searchTermDisplay.textContent = '';
            }
        }

        // Handle search form submission
        if (searchForm) {
            searchForm.addEventListener('submit', async (e) => {
                e.preventDefault();
                const term = searchBox?.value || '';
                if (term.trim()) {
                    globalSearchService.setSearchTerm(term.trim());
                    if (searchTermDisplay) {
                        searchTermDisplay.textContent = term.trim();
                    }
                    // Update URL without reload
                    const url = new URL(window.location.href);
                    url.searchParams.set('term', term.trim());
                    window.history.pushState({}, '', url.toString());
                    await performSearch();
                }
            });
        }

        // Function to update the results count
        const updateResultsCount = () => {
            if (resultsCountSpan) {
                const reportedTotalCount = globalSearchService.context.totalCount;               
                resultsCountSpan.textContent = reportedTotalCount.toString();
            }
        };

        // Function to perform search
        const performSearch = async () => {
            await globalSearchService.search(
                globalSearchService.filters, 
                1, 
                null, 
                null, 
                languageService.getCurrentInShortFormat()
            );
            updateFiltersFromFacets();
        };

        // Helper function to extract facet array from various formats
        const extractFacetArray = (facetData: any): GlobalSearchFacet[] | undefined => {
            if (!facetData) return undefined;
            if (Array.isArray(facetData)) {
                return facetData as GlobalSearchFacet[];
            }
            if (typeof facetData === 'object' && 'typeCountPairs' in facetData && Array.isArray(facetData.typeCountPairs)) {
                return facetData.typeCountPairs as GlobalSearchFacet[];
            }
            return undefined;
        };

        // Function to update filters based on facets from search results
        const updateFiltersFromFacets = () => {
            const facets = globalSearchService.context.facets;
            
            // Show filter container if we have any facets
            const hasFilters = Object.keys(facets).length > 0;
            if (filterContainer) {
                if (hasFilters) {
                    filterContainer.classList.remove('filter-container--hidden');
                } else {
                    filterContainer.classList.add('filter-container--hidden');
                }
            }

            // Document Type Filter
            let documentTypeFacet: GlobalSearchFacet[] | undefined;
            // Check ContentTypeAlias first (stored by property name)
            documentTypeFacet = extractFacetArray(facets['ContentTypeAlias']);
            if (!documentTypeFacet) {
                // Check DocumentTypeFacet object
                documentTypeFacet = extractFacetArray(facets['DocumentTypeFacet']);
            }
            if (documentTypeFacet && documentTypeFacet.length > 0) {
                const container = document.querySelector('#content-type-filter') as HTMLElement;
                if (container) {
                    if (!filterInstances['documentType']) {
                        filterInstances['documentType'] = new SearchFilter(
                            container,
                            translationService,
                            {
                                filterLabel: translate('Hero.ContentType'),
                                placeholderLabel: translate('Hero.FindDocumentType'),
                                data: documentTypeFacet.map(f => ({ key: f.key, label: f.label, value: f.value }))
                            },
                            (selectedKeys: string[]) => {
                                globalSearchService.setFilters({
                                    ...globalSearchService.filters,
                                    documentTypes: selectedKeys
                                });
                                performSearch();
                            },
                            'content-type'
                        );
                        // Restore selected filters from service
                        const currentSelected = globalSearchService.filters.documentTypes || [];
                        if (currentSelected.length > 0) {
                            (filterInstances['documentType'] as SearchFilter).setSelectedFilters(currentSelected);
                        }
                    } else {
                        (filterInstances['documentType'] as SearchFilter).updateData(
                            documentTypeFacet.map(f => ({ key: f.key, label: f.label, value: f.value }))
                        );
                        // Restore selected filters from service
                        const currentSelected = globalSearchService.filters.documentTypes || [];
                        if (currentSelected.length > 0) {
                            (filterInstances['documentType'] as SearchFilter).setSelectedFilters(currentSelected);
                        }
                    }
                    container.classList.remove('filter-item--hidden');
                }
            }

            // Industry Filter
            let industryFacet: GlobalSearchFacet[] | undefined;
            industryFacet = extractFacetArray(facets['industry']);
            if (!industryFacet) {
                industryFacet = extractFacetArray(facets['IndustryFacet']);
            }
            if (industryFacet && industryFacet.length > 0) {
                const container = document.querySelector('#industry-filter') as HTMLElement;
                if (container) {
                    if (!filterInstances['industry']) {
                        filterInstances['industry'] = new SearchFilter(
                            container,
                            translationService,
                            {
                                filterLabel: translate('Hero.industry'),
                                placeholderLabel: translate('Hero.FindIndustry'),
                                data: industryFacet.map(f => ({ key: f.key, label: f.label, value: f.value }))
                            },
                            (selectedKeys: string[]) => {
                                globalSearchService.setFilters({
                                    ...globalSearchService.filters,
                                    industries: selectedKeys
                                });
                                performSearch();
                            },
                            'industry'
                        );
                        // Restore selected filters from service
                        const currentSelected = globalSearchService.filters.industries || [];
                        if (currentSelected.length > 0) {
                            (filterInstances['industry'] as SearchFilter).setSelectedFilters(currentSelected);
                        }
                    } else {
                        (filterInstances['industry'] as SearchFilter).updateData(
                            industryFacet.map(f => ({ key: f.key, label: f.label, value: f.value }))
                        );
                        // Restore selected filters from service
                        const currentSelected = globalSearchService.filters.industries || [];
                        if (currentSelected.length > 0) {
                            (filterInstances['industry'] as SearchFilter).setSelectedFilters(currentSelected);
                        }
                    }
                    container.classList.remove('filter-item--hidden');
                }
            }

            // Category Filter (with nested support)
            let categoryFacet: any[] | undefined;
            const taxonomyData = facets['taxonomy'] as any;
            if (taxonomyData) {
                if (Array.isArray(taxonomyData)) {
                    categoryFacet = taxonomyData as any[];
                } else if (typeof taxonomyData === 'object' && taxonomyData !== null && 'typeCountPairs' in taxonomyData) {
                    const typeCountPairs = (taxonomyData as { typeCountPairs?: any[] }).typeCountPairs;
                    if (Array.isArray(typeCountPairs)) {
                        categoryFacet = typeCountPairs;
                    }
                }
            }
            if (!categoryFacet) {
                const categoryFacetData = facets['CategoryFacet'] as any;
                if (categoryFacetData) {
                    if (Array.isArray(categoryFacetData)) {
                        categoryFacet = categoryFacetData;
                    } else if (typeof categoryFacetData === 'object' && categoryFacetData !== null && 'typeCountPairs' in categoryFacetData) {
                        const typeCountPairs = (categoryFacetData as { typeCountPairs?: any[] }).typeCountPairs;
                        if (Array.isArray(typeCountPairs)) {
                            categoryFacet = typeCountPairs;
                        }
                    }
                }
            }
            if (categoryFacet && categoryFacet.length > 0) {
                const container = document.querySelector('#category-filter') as HTMLElement;
                if (container) {
                    // Transform nested structure
                    const transformCategoryData = (items: any[]): any[] => {
                        return items.map(item => ({
                            key: item.key,
                            label: item.label,
                            value: item.value,
                            children: item.children ? transformCategoryData(item.children) : undefined,
                            hasChildren: item.hasChildren || (item.children && item.children.length > 0),
                            id: item.id,
                            parentId: item.parentId
                        }));
                    };

                    const categoryData = transformCategoryData(categoryFacet);
                    
                    if (!filterInstances['category']) {
                        filterInstances['category'] = new SearchFilter(
                            container,
                            translationService,
                            {
                                filterLabel: translate('Hero.Category'),
                                placeholderLabel: translate('Hero.FindCategory'),
                                data: categoryData
                            },
                            (selectedKeys: string[]) => {
                                globalSearchService.setFilters({
                                    ...globalSearchService.filters,
                                    categories: selectedKeys
                                });
                                performSearch();
                            },
                            'category'
                        );
                        // Restore selected filters from service
                        const currentSelected = globalSearchService.filters.categories || [];
                        if (currentSelected.length > 0) {
                            (filterInstances['category'] as SearchFilter).setSelectedFilters(currentSelected);
                        }
                    } else {
                        (filterInstances['category'] as SearchFilter).updateData(categoryData);
                        // Restore selected filters from service
                        const currentSelected = globalSearchService.filters.categories || [];
                        if (currentSelected.length > 0) {
                            (filterInstances['category'] as SearchFilter).setSelectedFilters(currentSelected);
                        }
                    }
                    container.classList.remove('filter-item--hidden');
                }
            }

            // Network Filter
            let networkFacet: GlobalSearchFacet[] | undefined;
            networkFacet = extractFacetArray(facets['network']);
            if (!networkFacet) {
                networkFacet = extractFacetArray(facets['NetworkFacet']);
            }
            if (networkFacet && networkFacet.length > 0) {
                const container = document.querySelector('#network-filter') as HTMLElement;
                if (container) {
                    if (!filterInstances['network']) {
                        filterInstances['network'] = new SearchFilter(
                            container,
                            translationService,
                            {
                                filterLabel: translate('Hero.Network'),
                                placeholderLabel: translate('Hero.FindNetwork'),
                                data: networkFacet.map(f => ({ key: f.key, label: f.label, value: f.value }))
                            },
                            (selectedKeys: string[]) => {
                                globalSearchService.setFilters({
                                    ...globalSearchService.filters,
                                    networks: selectedKeys
                                });
                                performSearch();
                            },
                            'network'
                        );
                        // Restore selected filters from service
                        const currentSelected = globalSearchService.filters.networks || [];
                        if (currentSelected.length > 0) {
                            (filterInstances['network'] as SearchFilter).setSelectedFilters(currentSelected);
                        }
                    } else {
                        (filterInstances['network'] as SearchFilter).updateData(
                            networkFacet.map(f => ({ key: f.key, label: f.label, value: f.value }))
                        );
                        // Restore selected filters from service
                        const currentSelected = globalSearchService.filters.networks || [];
                        if (currentSelected.length > 0) {
                            (filterInstances['network'] as SearchFilter).setSelectedFilters(currentSelected);
                        }
                    }
                    container.classList.remove('filter-item--hidden');
                }
            }

            // Courts Filter
            let courtsFacet: GlobalSearchFacet[] | undefined;
            courtsFacet = extractFacetArray(facets['caseName']);
            if (!courtsFacet) {
                courtsFacet = extractFacetArray(facets['courtsFacet']);
            }
            if (courtsFacet && courtsFacet.length > 0) {
                const container = document.querySelector('#courts-filter') as HTMLElement;
                if (container) {
                    // Ensure keys are strings for courts filter
                    const courtsData = courtsFacet.map(f => ({ 
                        key: String(f.key), 
                        label: f.label, 
                        value: f.value 
                    }));
                    
                    if (!filterInstances['courts']) {
                        filterInstances['courts'] = new SearchFilter(
                            container,
                            translationService,
                            {
                                filterLabel: translate('Hero.Courts') || 'Courts',
                                placeholderLabel: translate('Hero.FindCourts') || 'Find a court',
                                data: courtsData
                            },
                            (selectedKeys: string[]) => {
                                // Ensure courts are sent as strings and filter out empty values
                                const courtsArray = selectedKeys
                                    .map(k => String(k))
                                    .filter(k => k && k.trim() !== '');
                                
                                globalSearchService.setFilters({
                                    ...globalSearchService.filters,
                                    courts: courtsArray.length > 0 ? courtsArray : []
                                });
                                performSearch();
                            },
                            'courts'
                        );
                        // Restore selected filters from service - convert to strings
                        const currentSelected = (globalSearchService.filters.courts || []).map(c => String(c));
                        if (currentSelected.length > 0) {
                            (filterInstances['courts'] as SearchFilter).setSelectedFilters(currentSelected);
                        }
                    } else {
                        (filterInstances['courts'] as SearchFilter).updateData(courtsData);
                        // Restore selected filters from service - convert to strings
                        const currentSelected = (globalSearchService.filters.courts || []).map(c => String(c));
                        if (currentSelected.length > 0) {
                            (filterInstances['courts'] as SearchFilter).setSelectedFilters(currentSelected);
                        }
                    }
                    container.classList.remove('filter-item--hidden');
                }
            }

            // Date Range Filter
            const dateRangeContainer = document.querySelector('#date-range-filter-container') as HTMLElement;
            if (dateRangeContainer && !filterInstances['dateRange']) {
                filterInstances['dateRange'] = new DateRangeSearchFilter(
                    dateRangeContainer,
                    searchContext,
                    (dateRange: DateRange) => {
                        globalSearchService.setFilters({
                            ...globalSearchService.filters,
                            dateFrom: dateRange.dateFrom || null,
                            dateTo: dateRange.dateTo || null
                        });
                        performSearch();
                    }
                );
            }

            // Read Status Filter (if available)
            const readStatusContainer = document.querySelector('#read-status-filter') as HTMLElement;
            if (readStatusContainer && !filterInstances['readStatus']) {
                readStatusContainer.innerHTML = `
                    <button type="button" class="horizontal-filter__dropdown-btn dropdown-toggle" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                        <span>${translate('Hero.ReadStatus')}</span>
                    </button>
                    <button type="button" class="horizontal-filter__clear-btn read-status-clear-btn filter-clear-btn--hidden">
                        <svg class="icon" viewBox="0 0 32 32">
                            <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="/images/icons-sprite.svg#cross-32"></use>
                        </svg>
                        <span class="screen-reader-text">${translate('Hero.ClearFilter')}</span>
                    </button>
                    <div class="horizontal-filter__menu dropdown-menu">
                        <ul class="horizontal-filter__options">
                            <li class="horizontal-filter__item">
                                <button class="read-status-option" data-read="true">${translate('Hero.ReadStatus.Read')}</button>
                            </li>
                            <li class="horizontal-filter__item">
                                <button class="read-status-option" data-read="false">${translate('Hero.ReadStatus.Unread')}</button>
                            </li>
                        </ul>
                    </div>
                `;

                const readStatusBtn = readStatusContainer.querySelector('.read-status-option') as HTMLElement;
                const clearBtn = readStatusContainer.querySelector('.read-status-clear-btn') as HTMLElement;
                const dropdownBtn = readStatusContainer.querySelector('.horizontal-filter__dropdown-btn span') as HTMLElement;

                readStatusContainer.querySelectorAll('.read-status-option').forEach(btn => {
                    btn.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const isRead = (btn as HTMLElement).dataset.read === 'true';
                        globalSearchService.setFilters({
                            ...globalSearchService.filters,
                            onlyRead: isRead
                        });
                        if (dropdownBtn) {
                            dropdownBtn.textContent = isRead ? translate('Hero.ReadStatus.Read') : translate('Hero.ReadStatus.Unread');
                        }
                        if (clearBtn) {
                            clearBtn.classList.remove('filter-clear-btn--hidden');
                        }
                        readStatusContainer.classList.add('horizontal-filter--active');
                        performSearch();
                    });
                });

                clearBtn?.addEventListener('click', () => {
                    globalSearchService.setFilters({
                        ...globalSearchService.filters,
                        onlyRead: null
                    });
                    if (dropdownBtn) {
                        dropdownBtn.textContent = translate('Hero.ReadStatus');
                    }
                    clearBtn.classList.add('filter-clear-btn--hidden');
                    readStatusContainer.classList.remove('horizontal-filter--active');
                    performSearch();
                });

                readStatusContainer.classList.remove('filter-item--hidden');
            }

            // Update clear all button visibility
            const hasActiveFilters = globalSearchService.filtersSet();
            if (clearAllFiltersBtn) {
                if (hasActiveFilters) {
                    clearAllFiltersBtn.classList.remove('filter-clear-all--hidden');
                } else {
                    clearAllFiltersBtn.classList.add('filter-clear-all--hidden');
                }
            }
        };

        // Clear all filters handler
        if (clearAllFiltersBtn) {
            clearAllFiltersBtn.addEventListener('click', async () => {
                globalSearchService.resetFilters();
                globalSearchService.setSearchTerm('');
                if (searchBox) {
                    searchBox.value = '';
                }
                if (searchTermDisplay) {
                    searchTermDisplay.textContent = '';
                }
                
                // Clear all filter instances
                Object.values(filterInstances).forEach(instance => {
                    if (instance && typeof instance.clearFilter === 'function') {
                        instance.clearFilter();
                    }
                });

                await performSearch();
            });
        }

        // Initial search
        await performSearch();

        // Initial update of results count
        updateResultsCount();

        // Add observer to update results count when search context changes
        globalSearchService.addObserver(updateResultsCount);

        // Initialize article listing
        const articleListing = document.querySelector('.pwc-studio-article-listing') as HTMLElement;
        if (articleListing) {
            new ArticleListingCard(articleListing, globalSearchService);
        }
    });
};
